import CryptoJS from 'crypto-js';

export class EncryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EncryptionError';
  }
}

export class DecryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecryptionError';
  }
}

const getEncryptionKey = (): string => {
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || '';
  
  if (!encryptionKey) {
    throw new EncryptionError('ENCRYPTION_KEY not configured');
  }
  
  return encryptionKey;
};

export const encryptData = (data: string): string => {
  try {
    const fernetKey = getEncryptionKey();
    
    const keyBuffer = CryptoJS.enc.Base64.parse(fernetKey);
    const signingKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(0, 4), 16);
    const encryptionKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(4, 8), 16);
    
    const version = CryptoJS.lib.WordArray.create([0x80000000], 1);
    
    const timestamp = Math.floor(Date.now() / 1000);
    const timestampHigh = Math.floor(timestamp / 0x100000000);
    const timestampLow = timestamp >>> 0;
    const timestampBytes = CryptoJS.lib.WordArray.create([timestampHigh, timestampLow], 8);
    
    const iv = CryptoJS.lib.WordArray.random(16);
    
    const encrypted = CryptoJS.AES.encrypt(data, encryptionKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    const tokenWithoutHmac = version.clone()
      .concat(timestampBytes)
      .concat(iv)
      .concat(encrypted.ciphertext);
    
    const hmac = CryptoJS.HmacSHA256(tokenWithoutHmac, signingKey);
    const finalToken = tokenWithoutHmac.concat(hmac);
    
    return CryptoJS.enc.Base64.stringify(finalToken);
    
  } catch (error: any) {
    throw new EncryptionError(`Failed to encrypt data: ${error.message}`);
  }
};

export const encryptJSON = (data: any): string => {
  try {
    const jsonString = JSON.stringify(data);
    return encryptData(jsonString);
  } catch (error: any) {
    throw new EncryptionError(`Failed to encrypt JSON data: ${error.message}`);
  }
};

// Helper function to convert CryptoJS WordArray to Uint8Array
const wordArrayToByteArray = (wordArray: any): Uint8Array => {
  const bytes = new Uint8Array(wordArray.sigBytes);
  for (let i = 0; i < wordArray.sigBytes; i++) {
    const wordIdx = Math.floor(i / 4);
    const byteIdx = i % 4;
    bytes[i] = (wordArray.words[wordIdx] >>> (24 - byteIdx * 8)) & 0xff;
  }
  return bytes;
};

// Helper function to convert byte array to CryptoJS WordArray
const byteArrayToWordArray = (bytes: Uint8Array): any => {
  const words: number[] = [];
  for (let i = 0; i < bytes.length; i += 4) {
    words.push(
      (bytes[i] << 24) | 
      ((bytes[i + 1] || 0) << 16) | 
      ((bytes[i + 2] || 0) << 8) | 
      (bytes[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length);
};

// Browser-compatible Fernet decryption using CryptoJS
export const decryptDataWithFernet = (encryptedData: string): string => {
  try {
    const encryptionKey = getEncryptionKey();
    
    // Parse the Fernet token (Fernet uses URL-safe base64)
    // Convert URL-safe base64 to standard base64
    const standardBase64Token = encryptedData.replace(/-/g, '+').replace(/_/g, '/');
    const token = CryptoJS.enc.Base64.parse(standardBase64Token);
    
    // Parse the 32-byte Fernet key (also URL-safe base64)
    const standardBase64Key = encryptionKey.replace(/-/g, '+').replace(/_/g, '/');
    const keyBuffer = CryptoJS.enc.Base64.parse(standardBase64Key);
    
    if (keyBuffer.sigBytes !== 32) {
      throw new Error(`Invalid Fernet key length: ${keyBuffer.sigBytes} bytes, expected 32`);
    }
    
    // Split the key: first 16 bytes for signing, last 16 bytes for encryption
    const signingKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(0, 4), 16);
    const encryptKey = CryptoJS.lib.WordArray.create(keyBuffer.words.slice(4, 8), 16);
    
    // Fernet token structure:
    // Version (1 byte) + Timestamp (8 bytes) + IV (16 bytes) + Ciphertext + HMAC (32 bytes)
    const tokenLength = token.sigBytes;
    
    if (tokenLength < 57) { // 1 + 8 + 16 + 0 + 32 = minimum 57 bytes
      throw new Error(`Invalid Fernet token length: ${tokenLength} bytes`);
    }
    
    // Extract version (should be 0x80)
    const version = (token.words[0] >>> 24) & 0xFF;
    if (version !== 0x80) {
      throw new Error(`Invalid Fernet version: 0x${version.toString(16)}, expected 0x80`);
    }
    
    // Extract components - MUST use byte-level extraction for HMAC too!
    const hmacStart = tokenLength - 32;
    
    // Convert token to bytes first for proper HMAC extraction
    const allTokenBytes = wordArrayToByteArray(token);
    
    // Extract token without HMAC (first hmacStart bytes)
    const tokenWithoutHmacBytes = allTokenBytes.slice(0, hmacStart);
    const tokenWithoutHmac = byteArrayToWordArray(tokenWithoutHmacBytes);
    
    // Extract received HMAC (last 32 bytes)
    const receivedHmacBytes = allTokenBytes.slice(hmacStart);
    const receivedHmac = byteArrayToWordArray(receivedHmacBytes);
    
    // Verify HMAC
    const calculatedHmac = CryptoJS.HmacSHA256(tokenWithoutHmac, signingKey);
    
    if (calculatedHmac.toString() !== receivedHmac.toString()) {
      throw new Error('Fernet HMAC verification failed');
    }
    
    // Extract IV (bytes 9-24)
    // Fernet structure: [version:1][timestamp:8][IV:16][ciphertext:n][hmac:32]
    const ivStart = 9; // After version(1) + timestamp(8)
    
    // Extract IV bytes (16 bytes starting at position 9)
    const ivBytes = allTokenBytes.slice(ivStart, ivStart + 16);
    const iv = byteArrayToWordArray(ivBytes);
    
    // Extract ciphertext (bytes 25 to hmacStart)
    const ciphertextStart = 25; // After version(1) + timestamp(8) + IV(16)
    const ciphertextBytes = allTokenBytes.slice(ciphertextStart, hmacStart);
    const ciphertext = byteArrayToWordArray(ciphertextBytes);
    
    // Decrypt using AES-128-CBC
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext } as any,
      encryptKey,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Fernet decryption resulted in empty string');
    }
    
    return decryptedString;
    
  } catch (error: any) {
    throw new DecryptionError(`Failed to decrypt data with Fernet: ${error.message}`);
  }
};

export const decryptJSON = (encryptedData: string): any => {
  try {
    const decryptedString = decryptDataWithFernet(encryptedData);
    return JSON.parse(decryptedString);
  } catch (error: any) {
    if (error instanceof DecryptionError) {
      throw error;
    }
    throw new DecryptionError(`Failed to decrypt JSON data: ${error.message}`);
  }
};

// Response decryption helpers for API responses
export interface EncryptedResponse {
  data: string | { data: string };
}

export const isEncryptedResponse = (response: any): response is EncryptedResponse => {
  // Check for nested data structure: { data: "encrypted_string" }
  const isSimpleEncrypted = (
    response &&
    typeof response === 'object' &&
    typeof response.data === 'string' &&
    Object.keys(response).length === 1
  );
  
  // Check for nested structure: { data: { data: "encrypted_string" } }
  const isNestedEncrypted = (
    response &&
    typeof response === 'object' &&
    response.data &&
    typeof response.data === 'object' &&
    typeof response.data.data === 'string'
  );
  
  return isSimpleEncrypted || isNestedEncrypted;
};

export const decryptResponse = (response: any): any => {
  try {
    // Determine the encrypted data location
    let encryptedData: string;
    
    if (typeof response.data === 'string') {
      // Simple structure: { data: "encrypted_string" }
      encryptedData = response.data;
    } else if (response.data && typeof response.data.data === 'string') {
      // Nested structure: { data: { data: "encrypted_string" } }
      encryptedData = response.data.data;
    } else {
      throw new Error('Could not find encrypted data in response structure');
    }
    
    const decryptedData = decryptJSON(encryptedData);
    return decryptedData;
  } catch (error: any) {
    throw new DecryptionError(`Failed to decrypt API response: ${error.message}`);
  }
};


